import get from 'lodash.get';
import React, { useContext, useEffect, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';

import apiUtil from 'src/api/activation';
import CustomToast from 'src/components/CustomToast';
import Loader from 'src/components/Loader';
import ConflictModal from 'src/containers/BusinessOnboarding/Activation/ConflictModal';
import { AppContext } from 'src/containers/Core/AppContext';
import { AppState } from 'src/containers/Core/AppReducer';
import { POS_VERIFY_ERROR, POS_VERIFY_ERROR_TITLE } from 'src/containers/Core/Messages';
import { BUSINESS_ONBOARDING_STEP, OnboardingStepProcess } from '..';
import Card from './Card';
import POSModal from './POSModal';
import SuccessModal from './SuccessModal';

import './style.scss';

type ActivationProps = {
  setOnboardingStep: (step: BUSINESS_ONBOARDING_STEP, state?: OnboardingStepProcess) => void;
};

const Activation: React.FunctionComponent<ActivationProps> = ({ setOnboardingStep }) => {
  const [loading, setLoading] = useState(false);
  const [isManual, setIsManual] = useState<boolean>(false);
  const [submitloading, setSubmitLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState<PosIndentifierResponse>(null);
  const [cardDetails, setCardDetails] = useState<ActivationCard>(null);
  const [showPOSModal, setShowPOSModal] = useState(false);
  const { store } = useContext(AppContext);
  const { businessOnboardingDetails }: AppState = store;
  const [conflict, setConflict] = useState<ConflictIdentifierResponse>(get(businessOnboardingDetails, 'posState'));
  const businessUuid = get(businessOnboardingDetails, 'business.uuid');

  if (!businessUuid) {
    return null;
  }

  useEffect(() => {
    generateActivationCard();
  }, []);

  const generateActivationCard = async () => {
    try {
      setLoading(true);
      const response = await apiUtil.generateCardDetails(businessUuid);
      setCardDetails(response);
    } catch {
      //
    } finally {
      setLoading(false);
    }
  };

  const onProceed = async () => {
    try {
      setSubmitLoading(true);
      const response = await apiUtil.verifyPaymentToProceed(businessUuid);
      setShowSuccessModal(response);
    } catch (error) {
      if (error.response.status === 409) {
        setConflict(error.response.data);
      } else {
        toast(<CustomToast title={POS_VERIFY_ERROR_TITLE} description={POS_VERIFY_ERROR} />);
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="overflow-auto h-100 p-3">
      <SuccessModal
        showSuccessModal={showSuccessModal}
        onHide={() => setShowSuccessModal(null)}
        setOnboardingStep={setOnboardingStep}
        onConflict={setConflict}
      />
      <ConflictModal isManual={isManual} conflict={conflict} onHide={() => setConflict(null)} />
      <POSModal
        onHide={() => setShowPOSModal(false)}
        onConflict={setConflict}
        show={showPOSModal}
        setOnboardingStep={setOnboardingStep}
      />
      <div className="d-flex flex-column">
        <strong>Please enter this credit card information in your POS and charge $0.10.</strong>
        <small className="text-muted mb-1">
          This allows us to automatically gather your POS information and link to your business profile. Once done,
          please click Proceed.
        </small>
      </div>
      {cardDetails ? <Card cardDetails={cardDetails} /> : null}
      <div className="mt-3">
        <span>
          Unable to process the virtual card on your POS?
          <span
            className="royal-blue-color c-pointer ml-1"
            onClick={() => {
              setIsManual(true);
              setShowPOSModal(true);
            }}
          >
            Enter POS information manually.
          </span>
        </span>
      </div>
      <div>
        <Button
          className="mt-3"
          onClick={() => {
            setIsManual(false);
            onProceed();
          }}
        >
          <small>{submitloading ? <Spinner animation="border" size="sm" className="mx-3" /> : 'Proceed'}</small>
        </Button>
      </div>
    </div>
  );
};

export default Activation;
